import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaBolt, FaCheckCircle, FaExclamationCircle, FaPlus } from 'react-icons/fa';
import { CodeBlock, SecretReveal } from './ApiTab';
import { del, fail, formatWhen, get, patch, post } from './api';

const STATUS_TONE = { pending_verification: 'warning', active: 'success', disabled: 'muted' };

const nodeVerify = `import crypto from 'node:crypto';

// app.post('/webhooks/unanimity', express.raw({ type: 'application/json' }), handler)
function verify(rawBody, header, secret) {
  const parts = Object.fromEntries(header.split(',').map((p) => p.split('=')));
  if (Math.abs(Date.now() / 1000 - Number(parts.t)) > 300) return false;
  const expected = crypto.createHmac('sha256', secret).update(\`\${parts.t}.\${rawBody}\`).digest('hex');
  const want = Buffer.from(expected);
  const given = Buffer.from(parts.v1 || '');
  return given.length === want.length && crypto.timingSafeEqual(want, given);
}`;

const pythonVerify = `import hashlib, hmac, time

def verify(raw_body: bytes, header: str, secret: str) -> bool:
    parts = dict(p.split("=", 1) for p in header.split(","))
    if abs(time.time() - int(parts["t"])) > 300:
        return False
    expected = hmac.new(secret.encode(), f"{parts['t']}.".encode() + raw_body, hashlib.sha256).hexdigest()
    return hmac.compare_digest(expected, parts.get("v1", ""))`;

const examplePayload = `{
  "id": "evt_66f…_stall",
  "type": "parcel.problem",
  "created_at": "2026-10-05T14:02:11Z",
  "data": {
    "parcel": {
      "tracking_number": "6A12345678901",
      "carrier": "Colissimo",
      "status": "in_transit",
      "problem": {"kind": "stall", "label": "…", "detail": null, "detected_at": "…"},
      "last_event": {"at": "…", "text": "En cours d'acheminement"}
    },
    "orders": [{"id": "…", "source": "shopify", "external_id": "4501", "number": "#1001", "client_ref": null}]
  }
}`;

const TestOutcome = ({ result }) => {
    const { t } = useTranslation();
    if (!result) return null;
    return (
        <div className={`co-alert ${result.ok ? 'co-alert--success' : 'co-alert--error'}`} role="status">
            {result.ok ? <FaCheckCircle aria-hidden="true" /> : <FaExclamationCircle aria-hidden="true" />}{' '}
            {result.ok
                ? t('commerceOrders.testOk', 'Connexion vérifiée : HTTP {{code}} en {{ms}} ms. Le webhook est actif.', { code: result.status_code, ms: result.duration_ms })
                : t('commerceOrders.testFailed', 'Échec du test : {{error}}', { error: result.error || `HTTP ${result.status_code}` })}
            {result.response_excerpt && <pre className="co-excerpt">{result.response_excerpt}</pre>}
        </div>
    );
};

const Deliveries = ({ endpointId }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'fr';
    const [items, setItems] = useState(null);
    useEffect(() => {
        get(`/webhooks/${endpointId}/deliveries`).then((res) => setItems(res.data.items || [])).catch(() => setItems([]));
    }, [endpointId]);
    if (items === null) return <div className="co-loading">{t('commerceOrders.loading', 'Chargement…')}</div>;
    if (items.length === 0) return <p className="co-muted co-small">{t('commerceOrders.noDelivery', 'Aucun événement envoyé pour l’instant : ils partent dès qu’un colis a un problème.')}</p>;
    return (
        <table className="co-table co-table--compact">
            <thead>
                <tr>
                    <th>{t('commerceOrders.deliveryEvent', 'Événement')}</th>
                    <th>{t('commerceOrders.deliveryStatus', 'État')}</th>
                    <th>{t('commerceOrders.deliveryAttempts', 'Essais')}</th>
                    <th>{t('commerceOrders.deliveryWhen', 'Créé')}</th>
                </tr>
            </thead>
            <tbody>
                {items.map((d) => (
                    <tr key={d.id}>
                        <td className="co-mono" title={d.event_id}>{d.event_type}</td>
                        <td>
                            <span className={`co-chip co-chip--${d.status === 'delivered' ? 'success' : d.status === 'failed' ? 'danger' : 'muted'}`}>
                                {t(`commerceOrders.deliveryStatuses.${d.status}`, d.status)}
                            </span>
                            {d.last_error && d.status !== 'delivered' && <span className="co-muted co-small"> {d.last_error}</span>}
                        </td>
                        <td>{d.attempts}</td>
                        <td>{formatWhen(d.created_at, lang)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

const EndpointCard = ({ endpoint, isAdmin, onChanged }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'fr';
    const [busy, setBusy] = useState('');
    const [result, setResult] = useState(null);
    const [secret, setSecret] = useState(null);
    const [error, setError] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [showDeliveries, setShowDeliveries] = useState(false);

    const run = async (kind, fn) => {
        setBusy(kind);
        setError('');
        try {
            await fn();
        } catch (err) {
            setError(fail(err, t('commerceOrders.actionError', 'L’opération a échoué.')));
        } finally {
            setBusy('');
        }
    };

    const test = () => run('test', async () => { const res = await post(`/webhooks/${endpoint.id}/test`); setResult(res.data); onChanged(); });
    const rotate = () => run('rotate', async () => { const res = await post(`/webhooks/${endpoint.id}/rotate-secret`); setSecret(res.data.secret); setResult(null); onChanged(); });
    const toggle = () => run('toggle', async () => { await patch(`/webhooks/${endpoint.id}`, { enabled: endpoint.status === 'disabled' }); setResult(null); onChanged(); });
    const remove = () => run('delete', async () => { await del(`/webhooks/${endpoint.id}`); onChanged(); });

    const lastTest = endpoint.last_test;
    const lastDelivery = endpoint.last_delivery;
    return (
        <article className="co-endpoint">
            <header className="co-endpoint-head">
                <div>
                    <span className="co-mono co-endpoint-url">{endpoint.url}</span>
                    {endpoint.description && <span className="co-muted"> · {endpoint.description}</span>}
                </div>
                <span className={`co-chip co-chip--${STATUS_TONE[endpoint.status] || 'muted'}`}>
                    {t(`commerceOrders.webhookStatuses.${endpoint.status}`, endpoint.status)}
                </span>
            </header>
            {endpoint.status === 'pending_verification' && (
                <p className="co-hint co-hint--block">{t('commerceOrders.pendingHint', 'Configurez le secret dans votre application, puis cliquez « Tester » : le webhook reçoit les événements dès qu’il répond 2xx.')}</p>
            )}
            <dl className="co-dl co-dl--inline">
                <dt>{t('commerceOrders.secret', 'Secret')}</dt><dd className="co-mono">{endpoint.secret_hint}</dd>
                <dt>{t('commerceOrders.lastTest', 'Dernier test')}</dt>
                <dd>{lastTest ? `${formatWhen(lastTest.at, lang)} · ${lastTest.ok ? 'OK' : (lastTest.error || `HTTP ${lastTest.status_code}`)}` : '—'}</dd>
                <dt>{t('commerceOrders.lastDelivery', 'Dernier envoi')}</dt>
                <dd>{lastDelivery ? `${formatWhen(lastDelivery.at, lang)} · ${lastDelivery.ok ? `HTTP ${lastDelivery.status_code}` : lastDelivery.error}` : '—'}</dd>
                {endpoint.consecutive_failures > 0 && (<><dt>{t('commerceOrders.failures', 'Échecs consécutifs')}</dt><dd>{endpoint.consecutive_failures}</dd></>)}
            </dl>
            {secret && (
                <SecretReveal title={t('commerceOrders.newSecret', 'Nouveau secret')} value={secret} onDone={() => setSecret(null)}
                    hint={t('commerceOrders.secretOnce', 'Copiez-le dans votre application maintenant : il ne sera plus affiché. Puis cliquez « Tester » pour réactiver les envois.')} />
            )}
            <TestOutcome result={result} />
            {error && <div className="co-alert co-alert--error" role="alert">{error}</div>}
            <div className="co-actions">
                {isAdmin && (
                    <>
                        <button type="button" className="u-btn u-btn--primary" onClick={test} disabled={Boolean(busy) || endpoint.status === 'disabled'}>
                            <FaBolt aria-hidden="true" /> {busy === 'test' ? t('commerceOrders.testing', 'Test…') : t('commerceOrders.test', 'Tester')}
                        </button>
                        <button type="button" className="u-btn u-btn--secondary" onClick={rotate} disabled={Boolean(busy)}>{t('commerceOrders.rotate', 'Régénérer le secret')}</button>
                        <button type="button" className="u-btn u-btn--secondary" onClick={toggle} disabled={Boolean(busy)}>
                            {endpoint.status === 'disabled' ? t('commerceOrders.enable', 'Réactiver') : t('commerceOrders.pause', 'Mettre en pause')}
                        </button>
                    </>
                )}
                <button type="button" className="co-link" onClick={() => setShowDeliveries(!showDeliveries)}>
                    {showDeliveries ? t('commerceOrders.hideDeliveries', 'Masquer les envois') : t('commerceOrders.showDeliveries', 'Voir les envois')}
                </button>
                {isAdmin && (confirmDelete ? (
                    <span className="co-confirm">
                        <button type="button" className="co-link co-link--danger" onClick={remove}>{t('commerceOrders.confirmDelete', 'Supprimer définitivement')}</button>
                        <button type="button" className="co-link" onClick={() => setConfirmDelete(false)}>{t('commerceOrders.cancel', 'Annuler')}</button>
                    </span>
                ) : (
                    <button type="button" className="co-link co-link--danger" onClick={() => setConfirmDelete(true)}>{t('commerceOrders.delete', 'Supprimer')}</button>
                ))}
            </div>
            {showDeliveries && <Deliveries endpointId={endpoint.id} />}
        </article>
    );
};

const WebhooksTab = ({ isAdmin }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [form, setForm] = useState({ url: '', description: '' });
    const [created, setCreated] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await get('/webhooks');
            setData(res.data);
        } catch (err) {
            setError(fail(err, t('commerceOrders.webhooksError', 'Impossible de charger les webhooks.')));
            setData({ items: [] });
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const create = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            const res = await post('/webhooks', { url: form.url.trim(), description: form.description.trim() || null, events: ['parcel.problem'] });
            setCreated(res.data);
            setForm({ url: '', description: '' });
            load();
        } catch (err) {
            setError(fail(err, t('commerceOrders.webhookCreateError', 'Impossible d’ajouter le webhook.')));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="co-stack">
            <section className="co-card co-pad">
                <header className="co-section-head">
                    <FaBolt aria-hidden="true" />
                    <div>
                        <h3>{t('commerceOrders.webhooksTitle', 'Webhooks')}</h3>
                        <p className="co-muted">{t('commerceOrders.webhooksIntro', 'Recevez un appel signé sur votre serveur dès qu’un de vos colis a un problème (retard anormal, colis silencieux, incident transporteur, perte présumée).')}</p>
                    </div>
                </header>
                {error && <div className="co-alert co-alert--error" role="alert">{error}</div>}
                {created && (
                    <SecretReveal title={t('commerceOrders.webhookCreated', 'Webhook ajouté — votre secret de signature')} value={created.secret}
                        hint={t('commerceOrders.webhookSecretHint', 'Enregistrez ce secret dans votre application (il ne sera plus affiché), vérifiez la signature de chaque appel, puis cliquez « Tester » sur le webhook.')}
                        onDone={() => setCreated(null)} />
                )}
                {data === null ? <div className="co-loading">{t('commerceOrders.loading', 'Chargement…')}</div> : (
                    <div className="co-stack">
                        {(data.items || []).map((endpoint) => (
                            <EndpointCard key={endpoint.id} endpoint={endpoint} isAdmin={isAdmin} onChanged={load} />
                        ))}
                        {(data.items || []).length === 0 && <p className="co-muted">{t('commerceOrders.noWebhook', 'Aucun webhook pour l’instant.')}</p>}
                    </div>
                )}
                {isAdmin ? (
                    <form className="co-inline-form" onSubmit={create}>
                        <input type="url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
                            placeholder="https://api.votre-domaine.fr/webhooks/unanimity" required />
                        <input type="text" value={form.description} maxLength={200} onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder={t('commerceOrders.webhookDescription', 'Description (facultatif)')} />
                        <button type="submit" className="u-btn u-btn--primary" disabled={busy || !form.url.trim().startsWith('https://')}>
                            <FaPlus aria-hidden="true" /> {t('commerceOrders.addWebhook', 'Ajouter')}
                        </button>
                    </form>
                ) : <p className="co-muted co-small">{t('commerceOrders.adminOnlyWebhooks', 'Seul un administrateur peut ajouter ou modifier des webhooks.')}</p>}
            </section>

            <section className="co-card co-pad co-docs">
                <h3>{t('commerceOrders.verifyTitle', 'Vérifier les appels')}</h3>
                <p>{t('commerceOrders.verifyIntro', 'Chaque appel est un POST JSON signé. L’en-tête Unanimity-Signature vaut t=<horodatage>,v1=<HMAC-SHA256 hexadécimal du texte « t.corps brut » avec votre secret>. Refusez un appel dont la signature ne correspond pas ou dont l’horodatage a plus de 5 minutes, et répondez 2xx en moins de 10 secondes : sinon l’envoi est retenté (8 essais sur 24 h). Les redirections ne sont pas suivies.')}</p>
                <ul className="co-facts">
                    <li><strong>{t('commerceOrders.headers', 'En-têtes')}</strong> <code>Unanimity-Signature</code> <code>Unanimity-Event-Id</code> <code>Unanimity-Event-Type</code></li>
                    <li><strong>{t('commerceOrders.events', 'Événements')}</strong> <code>parcel.problem</code> · <code>webhook.test</code> ({t('commerceOrders.testEventNote', 'bouton Tester')})</li>
                    <li><strong>{t('commerceOrders.idempotency', 'Doublons')}</strong> {t('commerceOrders.idempotencyValue', 'un même événement peut arriver deux fois après une reprise : dédoublonnez sur son id.')}</li>
                </ul>
                <CodeBlock label={t('commerceOrders.examplePayload', 'Exemple — parcel.problem')} code={examplePayload} />
                <CodeBlock label="Node.js" code={nodeVerify} />
                <CodeBlock label="Python" code={pythonVerify} />
            </section>
        </div>
    );
};

export default WebhooksTab;
